"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, FileSpreadsheet, FileText, GraduationCap, Microscope, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const servicesList = [{
  id: 1,
  title: "On-Site Instrument Qualification",
  desc: "Complete IQ/OQ/PQ protocols tailored to regulatory GMP & pharmacopeia standards."
}, {
  id: 2,
  title: "Preventive Maintenance & Calibration",
  desc: "Scheduled recalibration, optical alignment, and rapid spare-part servicing."
}, {
  id: 3,
  title: "Operator & Analyst Certification",
  desc: "Hands-on application training for lab technicians and process engineers."
}];
const resourcesList = [{
  id: 1,
  title: "PAT & NIR Method Application Notes",
  desc: "Peer-reviewed methodologies for real-time blend uniformity and drying endpoint verification."
}, {
  id: 2,
  title: "Regulatory Compliance Whitepapers",
  desc: "Guidance on 21 CFR Part 11, data integrity, and handheld Raman field screening."
}, {
  id: 3,
  title: "Sample Feasibility & Case Studies",
  desc: "Technical data packages and spectra libraries across chemical and pharmaceutical workflows."
}];
export default function ResourcesSplit() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="resourcessplit" data-nav="light" className="bg-muted py-20 md:py-28 text-foreground">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">
            Knowledge & Expertise
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Complete Lifecycle Support & Technical Knowledge
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Ensure long-term system reliability and access analytical data
            packages, user manuals, and method transfer guidelines for your
            analytical instrumentation.
          </p>
        </div>

        {/* 6/6 Two-Panel Grid */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          {/* Left Panel: Services */}
          <motion.div variants={cardVariants} className="flex">
            <Card className="relative flex flex-col justify-between w-full overflow-hidden bg-card border-border shadow-sm group transition-[box-shadow,transform] duration-300 hover:shadow-md">
              {/* Orange Top-Accent on Hover */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-primary transition-colors duration-300" />

              <div>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-md bg-secondary text-primary">
                      <Wrench className="size-5 text-primary" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Technical Services
                    </span>
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-default font-bold text-card-foreground">
                    Specialized Field Training & Maintenance Services
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground mt-2">
                    Factory-trained specialists delivering field calibration,
                    lifecycle maintenance, and certified operator training
                    across Ireland and the UK.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 pt-2">
                  <div className="space-y-3">
                    {servicesList.map((item, idx) => <div key={item.id} data-index={idx} className="flex items-start gap-3 p-3 rounded-md bg-secondary/50 border border-border/50">
                        <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-foreground">
                            {item.title}
                          </h4>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                            {item.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </div>

              <CardFooter className="pt-4 border-t border-border mt-6">
                <Button asChild variant="default" className="w-full sm:w-auto gap-2 group/btn">
                  <Link to="/ServicesPage">
                    Explore Services
                    <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Right Panel: Resources */}
          <motion.div variants={cardVariants} className="flex">
            <Card className="relative flex flex-col justify-between w-full overflow-hidden bg-card border-border shadow-sm group transition-[box-shadow,transform] duration-300 hover:shadow-md">
              {/* Orange Top-Accent on Hover */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-primary transition-colors duration-300" />

              <div>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-md bg-secondary text-primary">
                      <BookOpen className="size-5 text-primary" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Knowledge Hub
                    </span>
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-default font-bold text-card-foreground">
                    Technical Resources & Application Notes
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground mt-2">
                    Access analytical methodologies, regulatory frameworks,
                    case studies, and detailed spectral reference libraries.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 pt-2">
                  <div className="space-y-3">
                    {resourcesList.map((item, idx) => <div key={item.id} data-index={idx} className="flex items-start gap-3 p-3 rounded-md bg-secondary/50 border border-border/50">
                        <FileText className="size-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-foreground">
                            {item.title}
                          </h4>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                            {item.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </div>

              <CardFooter className="pt-4 border-t border-border mt-6">
                <Button asChild variant="default" className="w-full sm:w-auto gap-2 group/btn">
                  <Link to="/ResourcesPage">
                    View Knowledge Base
                    <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}